import React, { useMemo, useTransition } from 'react'

import { isUppercase } from '@syconium/little-miss-figgy/dist/lib/utils/isUppercase'

import { useCollectionFilterQueryParamsContext } from '../../../../../../../../../brunswick/containers/CollectionFilterQueryParamsContext'
import { trackEvent } from '../../../../../../../../../lib/analytics'
import { VisualFilterTile } from '../../../../../../../../../types/graphql'
import { LayoutVariant } from '../../../../../../../TileLayout'
import { useCollectionFiltersContext } from '../../../containers/CollectionFiltersContext'
import { coreStylesVisibleTilesDesktop, coreStylesVisibleTilesMobile } from '../VisualFilters'

import {
  Body,
  FilterCtaText,
  FilterDescription,
  Image,
  ImageDisabled,
  ImageWrapper,
} from './styles'

interface Props extends VisualFilterTile {
  imagePriority: 'preload' | 'lazy' | 'default'
  desktopLayout: LayoutVariant
}

export const CoreStylesVisualFilterSectionTile: React.FC<Props> = ({
  ctaText,
  image,
  id,
  value,
  filter: filterFromField,
  filterSpec,
  description,
  imagePriority = 'default',
  desktopLayout,
}) => {
  const { updateFilterSelection } = useCollectionFilterQueryParamsContext()
  const { filters } = useCollectionFiltersContext()
  const { selectedFilters } = useCollectionFilterQueryParamsContext()
  const [isUpdateFiltersTransitionPending, startUpdateFiltersTransition] = useTransition()
  const isChecked = useMemo(() => {
    const includedInFilters = Object.values(selectedFilters).flat().includes(value)
    return isUpdateFiltersTransitionPending ? !includedInFilters : includedInFilters
  }, [isUpdateFiltersTransitionPending, selectedFilters, value])
  const filter = filterSpec?.name || filterFromField || ''
  const getVisualFilterType = useMemo(
    () => filters?.find(f => f.displayName.toLocaleLowerCase() === filter),
    [filters, filter]
  )

  const isDisabled = useMemo(
    () => getVisualFilterType?.filterValues?.some(o => o.value === value && o.state === 'DISABLED'),
    [getVisualFilterType, value]
  )

  const toggleChecked = () => {
    if (isUpdateFiltersTransitionPending) return
    startUpdateFiltersTransition(() => {
      updateFilterSelection(filter, value, !isChecked)
    })
  }

  const selectedFilterValuesAsString = useMemo(() => {
    return Object.values(selectedFilters)
      .map(filterValues => filterValues.join(','))
      .join(',')
  }, [selectedFilters])

  return (
    <Body $isChecked={isChecked} $layoutDesktop={desktopLayout}>
      {!isDisabled ? (
        <label
          key={id}
          htmlFor={`checkbox-${id}`}
          {...trackEvent({
            customAttributes: {
              'data-heap-rolling-filters': selectedFilterValuesAsString,
              'data-heap-visual-filter-value': value,
              'data-heap-filter-type': 'visual',
            },
            category: 'page-tile-section-tile',
            action: 'click',
            label: value,
          })}
          style={{ position: 'relative' }}
        >
          <input
            type='checkbox'
            id={`checkbox-${id}`}
            style={{ opacity: '0', position: 'absolute', inset: '0' }}
            checked={isChecked}
            onChange={toggleChecked}
          />
          <ImageWrapper $isChecked={isChecked}>
            <Image
              alt={''}
              loading={imagePriority === 'lazy' ? 'lazy' : 'default'}
              src={image}
              aspectRatios={{
                sm: 20 / 26,
                md: 20 / 26,
              }}
              widths={{
                unit: 'vw',
                sm: 100 / coreStylesVisibleTilesMobile,
                md: 100 / coreStylesVisibleTilesDesktop,
              }}
              fetchPriority={imagePriority === 'preload' ? 'high' : 'auto'}
            />
          </ImageWrapper>
          <FilterCtaText
            isUppercase={isUppercase(ctaText)}
            isDisabled={isDisabled || false}
            isChecked={isChecked}
            onClick={() => {
              if (!isDisabled) toggleChecked()
            }}
          >
            {ctaText}
          </FilterCtaText>
          {description ? (
            <FilterDescription
              isUppercase={isUppercase(description)}
              isDisabled={isDisabled || false}
              isChecked={isChecked}
            >
              {description}
            </FilterDescription>
          ) : null}
        </label>
      ) : (
        <ImageDisabled
          alt={ctaText}
          src={image}
          loading={imagePriority === 'lazy' ? 'lazy' : 'default'}
          aspectRatios={{
            sm: 20 / 26,
            md: 20 / 26,
          }}
          widths={{
            unit: 'vw',
            sm: 100 / coreStylesVisibleTilesMobile,
            md: 100 / coreStylesVisibleTilesDesktop,
          }}
          fetchPriority={imagePriority === 'preload' ? 'high' : 'auto'}
        />
      )}
    </Body>
  )
}
